/* eslint-disable react/jsx-one-expression-per-line */
import { ReactElement, useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { navToError } from '@utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormType } from '@constants/forms';
import { iObligation, tile } from '@interfaces/interfaces';
import { iReviewFormValues } from '@services/ReviewService';
import { CLICK_PLUS_ICON } from '@services/ErrorMessages';
import { getObligationOptions } from '@api';
import { FormLoading, TextInput, TileRadioAccessible } from '@components/index';
import gridStyles from '@pages/forms/Grid.module.scss';

import AddIcon from '../../../../images/add.svg';
import TrashIcon from '../../../../images/trash.svg';

import styles from './ReviewStep12.module.scss';

function ReviewStep12(): ReactElement {
  const [loading, setLoading] = useState(true);
  // const [addedObs, setAddedObs] = useState<string[]>([]);
  const context = useFormikContext();
  const location = useLocation();
  const navigate = useNavigate();

  const { values }: { values: iReviewFormValues } = useFormikContext();

  const [authorisedOb, setObligationDetails] = useState({} as iObligation);

  const NoOtherFinesValue = (): ReactElement => (
    <>
      <div>No other fines</div>
    </>
  );

  const IncludeAllFinesValue = (): ReactElement => (
    <>
      <div>Include all eligible outstanding fines</div>
    </>
  );

  const ListFinesValue = (): ReactElement => (
    <>
      <div>List the fines to include in this application</div>
    </>
  );

  const TILE_DATA: tile[] = [
    {
      body: <NoOtherFinesValue />,
      value: 'noOtherFines'
    },
    {
      body: <IncludeAllFinesValue />,
      value: 'includeAllFines',
    },
    {
      body: <ListFinesValue />,
      value: 'listFinesManually',
    },
  ];

  useEffect(() => {
    try {
      setLoading(true);
      const obNumFormIk = values.OBLIGATION_NUMBER;
      if (values.OBLIGATION_NUMBER) {
      getObligationOptions(obNumFormIk, FormType.REVIEW)
        .then((responseBody) => {
          if (responseBody && responseBody?.Authorised) {
            setObligationDetails(responseBody?.Authorised);
            context.setFieldValue('OBLIGATION_NUMBER', responseBody?.Authorised.Number);
            context.setFieldValue('AuthorisedObligation', responseBody?.Authorised);
            const [day, month, year] = responseBody.Authorised.OffenceDate.split('/').map(Number);
            context.setFieldValue('OFFENCE_DATE', new Date(year, month - 1, day));
            context.setFieldValue('InfringementNumber', responseBody?.Authorised.InfringementNumber);
          } else {
            navToError(navigate, location);
          }
          if (responseBody && responseBody?.Obligations) {
            context.setFieldValue('EligibleObligations', responseBody?.Obligations);
          }
          setLoading(false);
        })
        .catch(() => navToError(navigate, location));
      } else {
        navToError(navigate, location);
      }
    } catch (ex) {
      navToError(navigate, location);
    }
    return () => {
      setLoading(false);
    };
  }, [values.OBLIGATION_NUMBER]);

  useEffect(() => {
    if (values.areThereAnyAdditionalFines !== 'listFinesManually') context.setFieldValue('SelectedObligationsStep12', []);
  }, [values.areThereAnyAdditionalFines]);

  const addObligation = () => {
    context.validateField('obligationNumberToIncludeStep12');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const errorList = Object.fromEntries(Object.entries(context.errors).filter(([key, value]) => value !== CLICK_PLUS_ICON));
    const errorMessages = Object.keys(errorList).filter((key) => key === 'obligationNumberToIncludeStep12');
    if (errorMessages.length === 0) {
      values.SelectedObligationsStep12.push(values.obligationNumberToIncludeStep12);
      context.setFieldValue('obligationNumberToIncludeStep12', '');
      context.setFieldTouched('obligationNumberToIncludeStep12', false);
    }
  };
  const removeObligation = (obligationNumber: string) => {
    values.SelectedObligationsStep12 = values.SelectedObligationsStep12.filter((ob) => ob !== obligationNumber);
    context.setFieldValue('obligationNumberToIncludeStep12', values.obligationNumberToIncludeStep12);
  };

  return (
    <>
      {loading && <FormLoading />}
      {!loading && (
        <div className={gridStyles.form}>
          <div className="vfH2">Your fines</div>
          <p>You are requesting a review of the following fine:</p>
          <div className={styles.offenceDetails}>
            <div className={styles.detailHeadings}>
              {authorisedOb.Number && <span>Obligation #</span>}
              {authorisedOb.OffenceDate && <span>Offence Date</span>}
              {authorisedOb.OffenceDesc && <span>Offence Description</span>}
              {authorisedOb.AmountDue && <span>Amount</span>}
            </div>
            <div className={styles.detailValues}>
              {authorisedOb.Number && <span className={styles.obligationNumber}>{authorisedOb.Number}</span>}
              {authorisedOb.OffenceDate && <span>{authorisedOb.OffenceDate}</span>}
              {authorisedOb.OffenceDesc && <span>{authorisedOb.OffenceDesc}</span>}
              {authorisedOb.AmountDue && <span>${authorisedOb.AmountDue}</span>}
            </div>
            <div className={styles.detailTable}>
              <table className={styles.offenceTable}>
                <tbody>
                  {
                  authorisedOb.Number && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Obligation #</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.Number}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceDate && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Offence Date</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.OffenceDate}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceDesc && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Offence Description</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.OffenceDesc}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.AmountDue && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Amount</td>
                    <td className={styles.tdSecondColBackground}>${authorisedOb.AmountDue}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceLocation && (
                  <tr className={styles.hideInMobileView}>
                    <td>Offence Location</td>
                    <td className={styles.uppcaseText}>{authorisedOb.OffenceLocation}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceTime && (
                  <tr className={styles.hideInMobileView}>
                    <td>Time of Offence</td>
                    <td>{authorisedOb.OffenceTime}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.Registration && (
                  <tr className={styles.hideInMobileView}>
                    <td>Vehicle Registration</td>
                    <td>{authorisedOb.Registration}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceLocation && (
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>
                      Offence Location <br /> <span className={styles.tdSpanFont}>{authorisedOb.OffenceLocation}</span>
                    </td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceTime && (
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>
                      Time of Offence <br /> <span className={styles.tdSpanFont}>{authorisedOb.OffenceTime}</span>
                    </td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.Registration && (
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>
                      Vehicle Registration <br /> <span className={styles.tdSpanFont}>{authorisedOb.Registration}</span>
                    </td>
                  </tr>
                  )
                  }
                </tbody>
              </table>
            </div>
          </div>
          <br />
          <span>Would you like to add any other fines to this application?</span>
          <Field
            name="areThereAnyAdditionalFines"
            title=""
            component={TileRadioAccessible}
            tileData={TILE_DATA}
            orientationColumn
            isMandatory
            classname={styles.tile}
          />
          {values.areThereAnyAdditionalFines === 'listFinesManually' && (
          <div>
            <span>You can only include fines at the same stage as the fine above - check the most recent notice if you are not sure what stage a fine is at.</span>
            <p>You will need to complete a separate application for fines at other stages of the infringement lifecycle.</p>
            {values.SelectedObligationsStep12.length > 0 && <span>Entered obligation number(s) or infringement number(s):</span>}
            {values.SelectedObligationsStep12.map((obligation) => (
              <div className={styles.descriptionDiv} key={obligation}>
                <p>
                  <strong>{obligation}</strong>
                </p>
                <button type="button" onClick={() => removeObligation(obligation)} className={styles.removeDiv}>
                  <img src={TrashIcon} alt="trash icon" />
                </button>
              </div>
            ))}
            <div className={styles.obligationNumberContainer}>
              <div className={styles.obligationNumberField}>
                <Field
                  name="obligationNumberToIncludeStep12"
                  component={TextInput}
                  title="Obligation number or infringement number"
                  autocomplete="off"
                  isMandatory
                />
              </div>
              <button
                type="button"
                onClick={addObligation}
                className={styles.iconButton}
              >
                <img
                  src={AddIcon}
                  alt="Plus Icon"
                />
              </button>
            </div>
          </div>
          )}
          <p className={styles.middleNoteSection}>If you want to apply for a review for other fine(s) under a different review ground, you will need to complete a separate application.</p>
        </div>
      )}
    </>
  );
}

export default ReviewStep12;
